import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { normLang } from "@/i18n";
import api, { apiError, money } from "@/lib/api";
import { toast } from "sonner";
import {
  ArrowLeft, Gift, Wallet, Clock, Copy, Share2, ArrowDownLeft, ArrowDownRight,
  Users, TrendingUp, Network, History, Send, FileText, X,
} from "lucide-react";
import { WithdrawalReceipt } from "@/components/shared/WithdrawalReceipt";

const TXT = {
  fr: {
    title: "Parrainage & Bonus", subtitle: "Vos revenus partenaires — distincts de votre solde Achats.",
    backClient: "Retour à mon compte client",
    availTitle: "Solde disponible (retirable)", withdraw: "Retirer",
    pendingTitle: "Solde en attente (hold 7 j)", pendingNote: "En attente — non retirable tant que non mûri.",
    referral: "Parrainage", binaryBonus: "Bonus Binaire",
    available: "Disponible", pending: "En attente", totalGenerated: "Total généré",
    linksTitle: "Vos liens de parrainage",
    linkLabel: (side, dir) => `Lien ${side} — sponsor + placement ${dir}`, left: "gauche", right: "droit",
    copy: "Copier", share: "Partager", linkCopied: "Lien copié", copyFail: "Copie impossible", shareTitle: "Rejoignez-moi sur TWP",
    volumesTitle: "Volumes Binaire",
    volLeft: "Volume gauche", volRight: "Volume droite", matched: "Correspondance disponible",
    carryLeft: "Report gauche", carryRight: "Report droite",
    volNote: "Valeurs calculées côté serveur (1 point équivaut à 1 USD de volume).",
    networkTitle: "Mon réseau direct", legLeft: "Jambe LEFT", legRight: "Jambe RIGHT",
    directPlacements: (n) => `${n} placement${n > 1 ? "s" : ""} direct${n > 1 ? "s" : ""}`,
    directRefs: (n) => `Filleuls directs (${n})`, noDirect: "Aucun filleul direct.",
    withdrawTitle: "Demander un retrait",
    fullName: "Nom complet (titulaire du paiement)", fullNamePh: "Prénom Nom",
    emailLabel: "Adresse e-mail",
    amountReq: "Montant demandé (USD)", method: "Méthode",
    country: "Pays", operator: "Opérateur", choose: "— Choisir —",
    mmNumber: "Numéro Mobile Money", mmNumberPh: "Ex: 0788123456",
    usdtNetwork: "Réseau USDT", uniqueNetwork: "(réseau unique)",
    usdtAddress: "Adresse USDT TRC20", usdtAddressPh: "Adresse TRC20 (jamais de clé privée)",
    reqAmount: "Montant demandé", fee: (p) => `Frais de retrait (${p}%)`, youReceive: "Vous recevrez",
    nameMatch: "Je confirme que mon nom complet correspond exactement au nom enregistré sur mon moyen de paiement.",
    availShort: "Disponible", min: "Min", dailyCap: "Plafond quotidien", remaining: "reste", delay: "Délai",
    verifyConfirm: "Vérifier et confirmer",
    wdHistory: "Historique des retraits",
    date: "Date", ref: "Réf.", amount: "Montant", statusH: "Statut", receipt: "Reçu",
    noWithdrawals: "Aucun retrait.",
    ledgerHistory: "Historique des mouvements",
    source: "Source", type: "Type", noMovements: "Aucun mouvement.",
    summaryTitle: "Résumé de la demande",
    network_: "Réseau", address: "Adresse", number: "Numéro",
    netToReceive: "Montant net à recevoir", indicativeDelay: "Délai indicatif",
    statusAfter: "Statut après confirmation:", pendingProcessing: "En attente de traitement",
    edit: "Modifier", sending: "Envoi…", confirmWithdraw: "Confirmer le retrait",
    defaultProcTime: "1–3 jours ouvrés", countryNames: {},
    // validation / toasts
    invalidAmount: "Montant invalide", minWithdraw: (m) => `Retrait minimum ${m}`,
    dailyRemainingMsg: (m) => `Plafond quotidien: reste ${m}`, aboveBalance: "Montant supérieur au solde disponible",
    nameRequired: "Nom complet requis (prénom et nom)", emailInvalid: "Adresse e-mail invalide",
    walletRequired: "Adresse wallet requise", mmRequired: "Numéro Mobile Money requis",
    countryOpRequired: "Pays et opérateur requis", nameMatchRequired: "Vous devez confirmer la correspondance du nom",
    wdSaved: "Demande de retrait enregistrée",
  },
  en: {
    title: "Referral & Bonus", subtitle: "Your partner earnings — separate from your Purchases balance.",
    backClient: "Back to my client account",
    availTitle: "Available balance (withdrawable)", withdraw: "Withdraw",
    pendingTitle: "Pending balance (7-day hold)", pendingNote: "Pending — not withdrawable until matured.",
    referral: "Referral", binaryBonus: "Binary Bonus",
    available: "Available", pending: "Pending", totalGenerated: "Total generated",
    linksTitle: "Your referral links",
    linkLabel: (side, dir) => `${side} link — sponsor + ${dir} placement`, left: "left", right: "right",
    copy: "Copy", share: "Share", linkCopied: "Link copied", copyFail: "Copy failed", shareTitle: "Join me on TWP",
    volumesTitle: "Binary volumes",
    volLeft: "LEFT volume", volRight: "RIGHT volume", matched: "Matched available",
    carryLeft: "LEFT carry", carryRight: "RIGHT carry",
    volNote: "Values computed server-side (1 point = 1 USD).",
    networkTitle: "My direct network", legLeft: "LEFT leg", legRight: "RIGHT leg",
    directPlacements: (n) => `${n} direct placement${n > 1 ? "s" : ""}`,
    directRefs: (n) => `Direct referrals (${n})`, noDirect: "No direct referral.",
    withdrawTitle: "Request a withdrawal",
    fullName: "Full name (payment holder)", fullNamePh: "First Last",
    emailLabel: "Email address",
    amountReq: "Requested amount (USD)", method: "Method",
    country: "Country", operator: "Operator", choose: "— Select —",
    mmNumber: "Mobile Money number", mmNumberPh: "e.g. 0788123456",
    usdtNetwork: "USDT network", uniqueNetwork: "(single network)",
    usdtAddress: "USDT TRC20 address", usdtAddressPh: "TRC20 address (never a private key)",
    reqAmount: "Requested amount", fee: (p) => `Withdrawal fee (${p}%)`, youReceive: "You will receive",
    nameMatch: "I confirm that my full name matches exactly the name registered on my payment method.",
    availShort: "Available", min: "Min", dailyCap: "Daily limit", remaining: "remaining", delay: "Delay",
    verifyConfirm: "Verify and confirm",
    wdHistory: "Withdrawal history",
    date: "Date", ref: "Ref.", amount: "Amount", statusH: "Status", receipt: "Receipt",
    noWithdrawals: "No withdrawal.",
    ledgerHistory: "Movement history",
    source: "Source", type: "Type", noMovements: "No movement.",
    summaryTitle: "Request summary",
    network_: "Network", address: "Address", number: "Number",
    netToReceive: "Net amount to receive", indicativeDelay: "Indicative delay",
    statusAfter: "Status after confirmation:", pendingProcessing: "Pending processing",
    edit: "Edit", sending: "Sending…", confirmWithdraw: "Confirm withdrawal",
    defaultProcTime: "1–3 business days",
    countryNames: { COD: "DR Congo", CIV: "Ivory Coast", SEN: "Senegal", CMR: "Cameroon", KEN: "Kenya", GHA: "Ghana", NGA: "Nigeria", UGA: "Uganda", TZA: "Tanzania", ZMB: "Zambia", RWA: "Rwanda" },
    invalidAmount: "Invalid amount", minWithdraw: (m) => `Minimum withdrawal ${m}`,
    dailyRemainingMsg: (m) => `Daily limit: ${m} remaining`, aboveBalance: "Amount exceeds available balance",
    nameRequired: "Full name required (first and last name)", emailInvalid: "Invalid email address",
    walletRequired: "Wallet address required", mmRequired: "Mobile Money number required",
    countryOpRequired: "Country and operator required", nameMatchRequired: "You must confirm the name match",
    wdSaved: "Withdrawal request recorded",
  },
};
const useT = () => { const { i18n } = useTranslation(); return TXT[normLang(i18n.language)] || TXT.fr; };

const pts = (n) => Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: 2 });
const ptsLabel = (n) => `${pts(n)} ${Math.abs(Number(n || 0)) < 2 ? "pt" : "pts"}`;

function Card({ title, icon: Icon, children, testid }) {
  return (
    <div data-testid={testid} className="rounded-2xl border border-border bg-card p-4 sm:p-5">
      <div className="mb-3 flex items-center gap-2 text-sm font-semibold text-foreground">
        {Icon && <Icon className="h-4 w-4 text-accent" />} {title}
      </div>
      {children}
    </div>
  );
}

function Balance({ label, value, tone, sub, testid, unit }) {
  const tones = { green: "text-emerald-500", amber: "text-amber-500", default: "text-foreground" };
  return (
    <div className="rounded-xl border border-border bg-secondary/40 p-3">
      <div className="text-[11px] uppercase tracking-wide text-muted-foreground">{label}</div>
      <div data-testid={testid} className={`mt-0.5 font-display text-xl font-bold ${tones[tone] || tones.default}`}>{unit === "pts" ? ptsLabel(value) : money(value)}</div>
      {sub != null && <div className="text-[11px] text-muted-foreground">{sub}</div>}
    </div>
  );
}

function LinkBox({ side, url, L }) {
  const copy = async () => { try { await navigator.clipboard.writeText(url); toast.success(L.linkCopied); } catch { toast.error(L.copyFail); } };
  const share = async () => {
    try { if (navigator.share) { await navigator.share({ title: L.shareTitle, url }); } else { await navigator.clipboard.writeText(url); toast.success(L.linkCopied); } }
    catch { /* cancelled */ }
  };
  const isLeft = side === "LEFT";
  return (
    <div className="rounded-xl border border-border bg-secondary/30 p-3" data-testid={`partner-link-${side.toLowerCase()}`}>
      <div className="mb-1 flex items-center gap-2 text-xs font-semibold">
        {isLeft ? <ArrowDownLeft className="h-4 w-4 text-sky-500" /> : <ArrowDownRight className="h-4 w-4 text-violet-500" />}
        <span className="font-normal text-muted-foreground">{L.linkLabel(side, isLeft ? L.left : L.right)}</span>
      </div>
      <div className="flex items-center gap-2">
        <input readOnly value={url || ""} className="min-w-0 flex-1 truncate rounded-lg border border-border bg-background px-2 py-1.5 text-xs text-muted-foreground" />
        <button onClick={copy} data-testid={`partner-copy-${side.toLowerCase()}`} className="flex h-8 w-8 items-center justify-center rounded-lg border border-border hover:bg-secondary" title={L.copy}><Copy className="h-4 w-4" /></button>
        <button onClick={share} data-testid={`partner-share-${side.toLowerCase()}`} className="flex h-8 w-8 items-center justify-center rounded-lg border border-border hover:bg-secondary" title={L.share}><Share2 className="h-4 w-4" /></button>
      </div>
    </div>
  );
}

export default function PartnerDashboard() {
  const L = useT();
  const [profile, setProfile] = useState(null);
  const [summary, setSummary] = useState(null);
  const [binary, setBinary] = useState(null);
  const [network, setNetwork] = useState(null);
  const [wd, setWd] = useState({ items: [], summary: null, min_withdrawal_usd: 0 });
  const [ledger, setLedger] = useState([]);
  const [config, setConfig] = useState(null);
  const [form, setForm] = useState({ amount: "", method: "mobile_money", coordinates: "", mm_country: "", mm_operator: "", usdt_network: "TRC20", full_name: "", email: "", name_match: false });
  const [submitting, setSubmitting] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [receipt, setReceipt] = useState(null);

  const load = useCallback(async () => {
    try {
      const [p, s, b, n, w, l, c] = await Promise.all([
        api.get("/partner/profile"), api.get("/partner/summary"), api.get("/partner/binary"),
        api.get("/partner/network"), api.get("/partner/withdrawals"), api.get("/partner/ledger"),
        api.get("/partner/withdrawal-config"),
      ]);
      setProfile(p.data); setSummary(s.data); setBinary(b.data);
      setNetwork(n.data); setWd(w.data); setLedger(l.data.entries || []); setConfig(c.data);
    } catch (e) { toast.error(apiError(e?.response?.data?.detail)); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const available = summary?.total_available || 0;
  const feeRate = config?.withdrawal_fee_rate || 0;
  const feePct = config?.withdrawal_fee_pct || 0;
  const minWd = config?.min_withdrawal_usd || 0;
  const reqAmt = parseFloat(form.amount) || 0;
  const feePreview = Math.round(reqAmt * feeRate * 100) / 100;
  const netPreview = Math.round((reqAmt - feePreview) * 100) / 100;
  const mmCountries = config?.mobile_money_countries || [];
  const selectedCountry = mmCountries.find((c) => c.country_code === form.mm_country);
  const dailyMax = config?.max_daily_withdrawal_usd || 0;
  const dailyRemaining = config?.daily_remaining ?? dailyMax;

  const validateForm = () => {
    if (!reqAmt || reqAmt <= 0) { toast.error(L.invalidAmount); return false; }
    if (reqAmt < minWd) { toast.error(L.minWithdraw(money(minWd))); return false; }
    if (dailyMax > 0 && reqAmt > dailyRemaining + 1e-6) { toast.error(L.dailyRemainingMsg(money(dailyRemaining))); return false; }
    if (reqAmt > available + 1e-6) { toast.error(L.aboveBalance); return false; }
    if (!form.full_name.trim() || !form.full_name.trim().includes(" ")) { toast.error(L.nameRequired); return false; }
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(form.email.trim())) { toast.error(L.emailInvalid); return false; }
    if (!form.coordinates.trim()) { toast.error(form.method === "usdt" ? L.walletRequired : L.mmRequired); return false; }
    if (form.method === "mobile_money" && (!form.mm_country || !form.mm_operator)) { toast.error(L.countryOpRequired); return false; }
    if (!form.name_match) { toast.error(L.nameMatchRequired); return false; }
    return true;
  };

  const openConfirm = () => { if (validateForm()) setConfirmOpen(true); };

  const submitWithdraw = async () => {
    setSubmitting(true);
    try {
      await api.post("/partner/withdrawals", {
        amount: reqAmt, method: form.method, coordinates: form.coordinates,
        mm_country: form.mm_country, mm_operator: form.mm_operator, usdt_network: "TRC20",
        full_name: form.full_name.trim(), email: form.email.trim(), name_match_confirmed: form.name_match,
        idempotency_key: `wd-${Date.now()}`,
      });
      toast.success(L.wdSaved);
      setConfirmOpen(false);
      setForm({ amount: "", method: "mobile_money", coordinates: "", mm_country: "", mm_operator: "", usdt_network: "TRC20", full_name: "", email: "", name_match: false });
      await load();
    } catch (e) { toast.error(apiError(e?.response?.data?.detail)); }
    setSubmitting(false);
  };

  const openReceipt = async (wid) => {
    try { const rc = await api.get(`/partner/withdrawals/${wid}/receipt`); setReceipt(rc.data); }
    catch (e) { toast.error(apiError(e?.response?.data?.detail)); }
  };

  const ref = summary?.referral || {};
  const bin = summary?.binary || {};

  return (
    <div className="space-y-5" data-testid="partner-dashboard">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="flex items-center gap-2 font-display text-2xl font-bold"><Gift className="h-6 w-6 text-accent" /> {L.title}</h1>
          <p className="text-sm text-muted-foreground">{L.subtitle}</p>
        </div>
        <Link to="/dashboard" data-testid="partner-back-client" className="flex items-center gap-1.5 rounded-lg border border-border px-3 py-2 text-sm hover:bg-secondary">
          <ArrowLeft className="h-4 w-4" /> {L.backClient}
        </Link>
      </div>

      {/* Totals */}
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <Card title={L.availTitle} icon={Wallet} testid="partner-available-card">
          <div className="flex items-center justify-between gap-3">
            <div data-testid="partner-total-available" className="font-display text-3xl font-bold text-emerald-500">{money(available)}</div>
            <a href="#partner-withdraw" className="rounded-lg bg-accent px-4 py-2 text-sm font-semibold text-white hover:opacity-90">{L.withdraw}</a>
          </div>
        </Card>
        <Card title={L.pendingTitle} icon={Clock} testid="partner-pending-card">
          <div data-testid="partner-total-pending" className="font-display text-3xl font-bold text-amber-500">{money(summary?.total_pending || 0)}</div>
          <div className="text-xs text-muted-foreground">{L.pendingNote}</div>
        </Card>
      </div>

      {/* Split sources */}
      <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
        <Card title={L.referral} icon={Users} testid="partner-referral-card">
          <div className="grid grid-cols-3 gap-2">
            <Balance label={L.available} value={ref.available} tone="green" testid="referral-available" />
            <Balance label={L.pending} value={ref.pending} tone="amber" testid="referral-pending" />
            <Balance label={L.totalGenerated} value={ref.total_generated} testid="referral-total" />
          </div>
        </Card>
        <Card title={L.binaryBonus} icon={TrendingUp} testid="partner-binary-card">
          <div className="grid grid-cols-3 gap-2">
            <Balance label={L.available} value={bin.available} tone="green" testid="binary-available" />
            <Balance label={L.pending} value={bin.pending} tone="amber" testid="binary-pending" />
            <Balance label={L.totalGenerated} value={bin.total_generated} testid="binary-total" />
          </div>
        </Card>
      </div>

      {/* Links */}
      <Card title={L.linksTitle} icon={Share2} testid="partner-links-card">
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
          <LinkBox side="LEFT" url={profile?.left_link} L={L} />
          <LinkBox side="RIGHT" url={profile?.right_link} L={L} />
        </div>
      </Card>

      {/* Binary figures + network */}
      <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
        <Card title={L.volumesTitle} icon={TrendingUp} testid="partner-volumes-card">
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
            <Balance label={L.volLeft} value={binary?.left_volume} unit="pts" testid="vol-left" />
            <Balance label={L.volRight} value={binary?.right_volume} unit="pts" testid="vol-right" />
            <Balance label={L.matched} value={binary?.matched_volume} unit="pts" tone="green" testid="vol-matched" />
            <Balance label={L.carryLeft} value={binary?.left_carry} unit="pts" testid="carry-left" />
            <Balance label={L.carryRight} value={binary?.right_carry} unit="pts" testid="carry-right" />
            <Balance label={L.binaryBonus} value={bin.total_generated} testid="vol-bonus" />
          </div>
          <p className="mt-2 text-[11px] text-muted-foreground">{L.volNote}</p>
        </Card>
        <Card title={L.networkTitle} icon={Network} testid="partner-network-card">
          <div className="mb-2 grid grid-cols-2 gap-2 text-sm">
            <div className="rounded-lg border border-border bg-secondary/30 p-2">
              <div className="flex items-center gap-1 text-xs text-muted-foreground"><ArrowDownLeft className="h-3.5 w-3.5 text-sky-500" /> {L.legLeft}</div>
              <div className="font-mono text-sm">{network?.left_child ? network.left_child.member_code : "—"}</div>
              <div className="text-[11px] text-muted-foreground">{L.directPlacements(network?.left_leg_direct || 0)}</div>
            </div>
            <div className="rounded-lg border border-border bg-secondary/30 p-2">
              <div className="flex items-center gap-1 text-xs text-muted-foreground"><ArrowDownRight className="h-3.5 w-3.5 text-violet-500" /> {L.legRight}</div>
              <div className="font-mono text-sm">{network?.right_child ? network.right_child.member_code : "—"}</div>
              <div className="text-[11px] text-muted-foreground">{L.directPlacements(network?.right_leg_direct || 0)}</div>
            </div>
          </div>
          <div className="text-[11px] uppercase tracking-wide text-muted-foreground">{L.directRefs(network?.direct_count || 0)}</div>
          <div className="mt-1 max-h-32 space-y-1 overflow-auto">
            {(network?.direct_referrals || []).map((d, i) => (
              <div key={i} className="flex items-center justify-between rounded-md border border-border px-2 py-1 text-xs">
                <span className="font-mono">{d.member_code}</span>
                <span className="text-muted-foreground">{d.placement_side || "—"} · G{d.generation ?? "—"} · {d.status}</span>
              </div>
            ))}
            {!network?.direct_referrals?.length && <div className="py-2 text-center text-xs text-muted-foreground">{L.noDirect}</div>}
          </div>
        </Card>
      </div>

      {/* Withdraw */}
      <Card title={L.withdrawTitle} icon={Send} testid="partner-withdraw-card">
        <div id="partner-withdraw" className="space-y-3">
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div>
              <label className="text-xs text-muted-foreground">{L.fullName}</label>
              <input data-testid="withdraw-full-name" value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })}
                className="mt-1 w-full rounded-lg border border-border bg-background px-3 py-2 text-sm" placeholder={L.fullNamePh} />
            </div>
            <div>
              <label className="text-xs text-muted-foreground">{L.emailLabel}</label>
              <input data-testid="withdraw-email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
                className="mt-1 w-full rounded-lg border border-border bg-background px-3 py-2 text-sm" placeholder="vous@exemple.com" />
            </div>
          </div>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div>
              <label className="text-xs text-muted-foreground">{L.amountReq}</label>
              <input data-testid="withdraw-amount" type="number" min="0" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })}
                className="mt-1 w-full rounded-lg border border-border bg-background px-3 py-2 text-sm" placeholder={`${L.min} ${money(minWd)} · Max ${money(available)}`} />
            </div>
            <div>
              <label className="text-xs text-muted-foreground">{L.method}</label>
              <select data-testid="withdraw-method" value={form.method} onChange={(e) => setForm({ ...form, method: e.target.value, coordinates: "", mm_country: "", mm_operator: "", usdt_network: "" })}
                className="mt-1 w-full rounded-lg border border-border bg-background px-3 py-2 text-sm">
                <option value="mobile_money">Mobile Money</option>
                <option value="usdt">USDT</option>
              </select>
            </div>
          </div>

          {form.method === "mobile_money" ? (
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
              <div>
                <label className="text-xs text-muted-foreground">{L.country}</label>
                <select data-testid="withdraw-mm-country" value={form.mm_country} onChange={(e) => setForm({ ...form, mm_country: e.target.value, mm_operator: "" })}
                  className="mt-1 w-full rounded-lg border border-border bg-background px-3 py-2 text-sm">
                  <option value="">{L.choose}</option>
                  {mmCountries.map((c) => <option key={c.country_code} value={c.country_code}>{L.countryNames[c.country_code] || c.country}</option>)}
                </select>
              </div>
              <div>
                <label className="text-xs text-muted-foreground">{L.operator}</label>
                <select data-testid="withdraw-mm-operator" value={form.mm_operator} onChange={(e) => setForm({ ...form, mm_operator: e.target.value })}
                  disabled={!selectedCountry} className="mt-1 w-full rounded-lg border border-border bg-background px-3 py-2 text-sm disabled:opacity-50">
                  <option value="">{L.choose}</option>
                  {(selectedCountry?.operators || []).map((o) => <option key={o} value={o}>{o}</option>)}
                </select>
              </div>
              <div>
                <label className="text-xs text-muted-foreground">{L.mmNumber}</label>
                <input data-testid="withdraw-coordinates" value={form.coordinates} onChange={(e) => setForm({ ...form, coordinates: e.target.value })}
                  className="mt-1 w-full rounded-lg border border-border bg-background px-3 py-2 text-sm" placeholder={L.mmNumberPh} />
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
              <div>
                <label className="text-xs text-muted-foreground">{L.usdtNetwork}</label>
                <div data-testid="withdraw-usdt-network" className="mt-1 flex w-full items-center gap-2 rounded-lg border border-border bg-secondary/40 px-3 py-2 text-sm font-semibold">
                  TRC20 <span className="text-[10px] font-normal text-muted-foreground">{L.uniqueNetwork}</span>
                </div>
              </div>
              <div className="sm:col-span-2">
                <label className="text-xs text-muted-foreground">{L.usdtAddress}</label>
                <input data-testid="withdraw-coordinates" value={form.coordinates} onChange={(e) => setForm({ ...form, coordinates: e.target.value })}
                  className="mt-1 w-full rounded-lg border border-border bg-background px-3 py-2 text-sm" placeholder={L.usdtAddressPh} />
              </div>
            </div>
          )}

          {/* Fee preview */}
          {reqAmt > 0 && (
            <div data-testid="withdraw-fee-preview" className="rounded-xl border border-border bg-secondary/40 p-3 text-sm">
              <div className="flex justify-between"><span className="text-muted-foreground">{L.reqAmount}</span><span>{money(reqAmt)}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">{L.fee(feePct)}</span><span className="text-rose-500">-{money(feePreview)}</span></div>
              <div className="mt-1 flex justify-between border-t border-border pt-1 font-semibold"><span>{L.youReceive}</span><span data-testid="withdraw-net" className="text-emerald-500">{money(netPreview)}</span></div>
            </div>
          )}

          {/* Mandatory name-match confirmation */}
          <label className="flex cursor-pointer items-start gap-2 rounded-xl border border-border bg-secondary/30 p-3 text-xs text-muted-foreground">
            <input type="checkbox" data-testid="withdraw-name-match" checked={form.name_match} onChange={(e) => setForm({ ...form, name_match: e.target.checked })} className="mt-0.5 h-4 w-4" />
            <span>{L.nameMatch}</span>
          </label>
        </div>
        <div className="mt-3 flex flex-wrap items-center justify-between gap-2">
          <div className="text-xs text-muted-foreground">
            {L.availShort}: <span className="font-semibold text-emerald-500">{money(available)}</span> · {L.min}: {money(minWd)}
            {dailyMax > 0 ? <> · {L.dailyCap}: {money(dailyMax)} ({L.remaining} <span className="font-semibold">{money(dailyRemaining)}</span>)</> : null}
            {config?.processing_time ? <> · {L.delay}: {config.processing_time === "1–3 jours ouvrés" ? L.defaultProcTime : config.processing_time}</> : null}
          </div>
          <button onClick={openConfirm} disabled={submitting} data-testid="withdraw-submit"
            className="rounded-lg bg-accent px-5 py-2 text-sm font-semibold text-white hover:opacity-90 disabled:opacity-50">
            {L.verifyConfirm}
          </button>
        </div>
      </Card>

      {/* Withdrawals history */}
      <Card title={L.wdHistory} icon={History} testid="partner-withdrawals-card">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-xs text-muted-foreground"><tr>
              <th className="px-2 py-1.5 text-left">{L.date}</th><th className="px-2 py-1.5 text-left">{L.ref}</th><th className="px-2 py-1.5 text-right">{L.amount}</th>
              <th className="px-2 py-1.5 text-left">{L.method}</th><th className="px-2 py-1.5 text-left">{L.statusH}</th><th className="px-2 py-1.5 text-right">{L.receipt}</th>
            </tr></thead>
            <tbody>
              {(wd.items || []).map((w) => (
                <tr key={w.id} className="border-t border-border" data-testid={`withdrawal-row-${w.id}`}>
                  <td className="px-2 py-1.5 text-xs text-muted-foreground">{(w.created_at || "").slice(0, 10)}</td>
                  <td className="px-2 py-1.5 font-mono text-xs">{w.reference || "—"}</td>
                  <td className="px-2 py-1.5 text-right">{money(w.amount)}</td>
                  <td className="px-2 py-1.5">{w.method === "usdt" ? "USDT" : "Mobile Money"}</td>
                  <td className="px-2 py-1.5"><span className="rounded-md bg-secondary px-2 py-0.5 text-xs">{w.status_label || w.status}</span></td>
                  <td className="px-2 py-1.5 text-right">
                    {w.status === "paid"
                      ? <button onClick={() => openReceipt(w.id)} data-testid={`withdrawal-receipt-${w.id}`} className="inline-flex items-center gap-1 rounded-md border border-border px-2 py-1 text-xs hover:bg-secondary"><FileText className="h-3.5 w-3.5" /> {L.receipt}</button>
                      : <span className="text-xs text-muted-foreground">—</span>}
                  </td>
                </tr>
              ))}
              {!wd.items?.length && <tr><td colSpan={6} className="px-2 py-4 text-center text-muted-foreground">{L.noWithdrawals}</td></tr>}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Ledger history */}
      <Card title={L.ledgerHistory} icon={History} testid="partner-ledger-card">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-xs text-muted-foreground"><tr>
              <th className="px-2 py-1.5 text-left">{L.date}</th><th className="px-2 py-1.5 text-left">{L.source}</th>
              <th className="px-2 py-1.5 text-left">{L.type}</th><th className="px-2 py-1.5 text-right">{L.amount}</th>
              <th className="px-2 py-1.5 text-left">{L.statusH}</th><th className="px-2 py-1.5 text-left">{L.ref}</th>
            </tr></thead>
            <tbody>
              {ledger.map((e) => (
                <tr key={e.id} className="border-t border-border" data-testid={`ledger-row-${e.id}`}>
                  <td className="px-2 py-1.5 text-xs text-muted-foreground">{(e.created_at || "").slice(0, 10)}</td>
                  <td className="px-2 py-1.5"><span className={`rounded-md px-2 py-0.5 text-xs ${e.source === "BINARY" ? "bg-violet-500/10 text-violet-500" : "bg-sky-500/10 text-sky-500"}`}>{e.source}</span></td>
                  <td className="px-2 py-1.5 text-xs">{e.kind}</td>
                  <td className={`px-2 py-1.5 text-right ${Number(e.amount) < 0 ? "text-rose-500" : "text-emerald-500"}`}>{money(e.amount)}</td>
                  <td className="px-2 py-1.5 text-xs">{e.state}</td>
                  <td className="px-2 py-1.5 text-xs text-muted-foreground">{e.order_number ? `#${e.order_number}` : "—"}</td>
                </tr>
              ))}
              {!ledger.length && <tr><td colSpan={6} className="px-2 py-4 text-center text-muted-foreground">{L.noMovements}</td></tr>}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Confirmation summary */}
      {confirmOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center overflow-y-auto bg-black/60 p-4" onClick={() => setConfirmOpen(false)} data-testid="withdraw-confirm-overlay">
          <div onClick={(e) => e.stopPropagation()} className="my-4 w-full max-w-md rounded-2xl border border-border bg-card p-5" data-testid="withdraw-confirm-modal">
            <div className="mb-3 flex items-center justify-between"><h3 className="font-display text-lg font-bold">{L.summaryTitle}</h3><button onClick={() => setConfirmOpen(false)}><X className="h-5 w-5" /></button></div>
            <div className="space-y-1 text-sm">
              <div className="flex justify-between"><span className="text-muted-foreground">{L.fullName}</span><span className="font-medium">{form.full_name}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">{L.emailLabel}</span><span className="font-medium">{form.email}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">{L.method}</span><span className="font-medium">{form.method === "usdt" ? "USDT" : "Mobile Money"}</span></div>
              {form.method === "usdt" ? (
                <>
                  <div className="flex justify-between"><span className="text-muted-foreground">{L.network_}</span><span className="font-medium">TRC20</span></div>
                  <div className="flex justify-between"><span className="text-muted-foreground">{L.address}</span><span className="max-w-[60%] truncate font-mono text-xs">{form.coordinates}</span></div>
                </>
              ) : (
                <>
                  <div className="flex justify-between"><span className="text-muted-foreground">{L.country}</span><span className="font-medium">{selectedCountry ? (L.countryNames[selectedCountry.country_code] || selectedCountry.country) : ""}</span></div>
                  <div className="flex justify-between"><span className="text-muted-foreground">{L.operator}</span><span className="font-medium">{form.mm_operator}</span></div>
                  <div className="flex justify-between"><span className="text-muted-foreground">{L.number}</span><span className="font-medium">{form.coordinates}</span></div>
                </>
              )}
              <div className="my-2 border-t border-border" />
              <div className="flex justify-between"><span className="text-muted-foreground">{L.reqAmount}</span><span className="font-medium">{money(reqAmt)}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">{L.fee(feePct)}</span><span className="text-rose-500">-{money(feePreview)}</span></div>
              <div className="flex justify-between border-t border-border pt-1 font-semibold"><span>{L.netToReceive}</span><span className="text-emerald-500">{money(netPreview)}</span></div>
              <div className="pt-1 text-xs text-muted-foreground">{L.indicativeDelay}: {(config?.processing_time && config.processing_time !== "1–3 jours ouvrés") ? config.processing_time : L.defaultProcTime}</div>
              <div className="pt-1 text-xs text-muted-foreground">{L.statusAfter} <span className="font-semibold text-amber-500">{L.pendingProcessing}</span></div>
            </div>
            <div className="mt-4 flex gap-2">
              <button onClick={() => setConfirmOpen(false)} className="flex-1 rounded-lg border border-border px-4 py-2 text-sm">{L.edit}</button>
              <button onClick={submitWithdraw} disabled={submitting} data-testid="withdraw-confirm-submit" className="flex-1 rounded-lg bg-accent px-4 py-2 text-sm font-semibold text-white disabled:opacity-50">{submitting ? L.sending : L.confirmWithdraw}</button>
            </div>
          </div>
        </div>
      )}

      {receipt && <WithdrawalReceipt receipt={receipt} onClose={() => setReceipt(null)} />}
    </div>
  );
}
